import { Navigation, Footer } from "../components/landing/Layout";
import { Button } from "../components/ui/button";
import { Mail, MapPin, Clock, MessageCircle } from "lucide-react";
import WhatsAppIcon from "../components/landing/WhatsAppIcon";
import { openWhatsApp } from "../utils/whatsappLinks";
import { WHATSAPP_NUMBER } from "../components/landing/constants";
import { useCMSContent } from "../hooks/useCMSContent";
import { useI18n } from "../i18n";

export default function Contact() {
  const { data: c } = useCMSContent("contact_json");
  const { t } = useI18n();
  const cfg = c || {};

  const showPlaceholder = (s) => (s && String(s).includes("[COMPLETAR POR ADMIN]"));

  return (
    <div className="min-h-screen bg-slate-50" data-testid="contact-page">
      <Navigation />

      <section className="bg-gradient-to-br from-[#0B2545] via-[#0F2942] to-[#0B2545] py-20 relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-64 h-64 bg-[#1B84FF]/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-16 -left-16 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-[#5BC0FF]/15 text-[#9FD8FF] px-4 py-2 rounded-full text-sm font-medium mb-6">
            <MessageCircle className="w-4 h-4" />
            {t("contact.badge")}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="contact-hero-title">
            {cfg.hero_title || "Contacto"}
          </h1>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto" data-testid="contact-hero-subtitle">
            {cfg.hero_subtitle || "¿Dudas sobre tu QR, tu pago o tu cuenta? Escríbenos."}
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-6">
          {/* WhatsApp big card */}
          <div className="md:col-span-2 bg-white rounded-3xl border border-slate-200 p-8 text-center shadow-sm hover:shadow-lg transition-shadow">
            <div className="w-16 h-16 rounded-full bg-[#25D366]/10 flex items-center justify-center mx-auto mb-4">
              <WhatsAppIcon className="w-8 h-8 text-[#25D366]" />
            </div>
            <h2 className="text-2xl font-bold text-[#0B2545] mb-2" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="contact-wa-label">
              {cfg.whatsapp_label || "WhatsApp"}
            </h2>
            <p className="text-slate-600 mb-1 text-lg font-medium" data-testid="contact-wa-display">{cfg.whatsapp_display || "+51 921 959 407"}</p>
            <p className="text-slate-400 text-sm mb-6" data-testid="contact-wa-hint">{cfg.whatsapp_hint || "Respuesta en horario hábil"}</p>
            <Button
              onClick={() => openWhatsApp(WHATSAPP_NUMBER, t("contact.wa_default_msg"))}
              className="rounded-full bg-[#25D366] hover:bg-[#20BD5A] text-white font-semibold px-8 py-6 text-lg shadow-lg shadow-[#25D366]/30"
              data-testid="contact-whatsapp-btn"
            >
              <WhatsAppIcon className="w-5 h-5 mr-2" />
              {cfg.whatsapp_button || "Escribir por WhatsApp"}
            </Button>
          </div>

          {/* Email */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-md transition-shadow" data-testid="contact-email-card">
            <div className="w-12 h-12 rounded-xl bg-[#EAF4FF] flex items-center justify-center mb-4">
              <Mail className="w-6 h-6 text-[#1B84FF]" />
            </div>
            <h3 className="font-bold text-[#0B2545] mb-1">{cfg.email_label || "Correo"}</h3>
            <p className={`text-sm ${showPlaceholder(cfg.email_display) ? "text-amber-600 italic" : "text-slate-600"}`} data-testid="contact-email-display">
              {cfg.email_display || "correo@avisocar.com"}
            </p>
          </div>

          {/* Location */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-md transition-shadow" data-testid="contact-location-card">
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-4">
              <MapPin className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="font-bold text-[#0B2545] mb-1">{cfg.location_label || "Ubicación"}</h3>
            <p className={`text-sm ${showPlaceholder(cfg.location_display) ? "text-amber-600 italic" : "text-slate-600"}`} data-testid="contact-location-display">
              {cfg.location_display || "Perú"}
            </p>
          </div>

          {/* Hours */}
          <div className="md:col-span-2 bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-md transition-shadow" data-testid="contact-hours-card">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#0B2545]/10 flex items-center justify-center flex-shrink-0">
                <Clock className="w-6 h-6 text-[#0B2545]" />
              </div>
              <div>
                <h3 className="font-bold text-[#0B2545] mb-1">{cfg.office_hours_label || "Horario"}</h3>
                <p className="text-slate-600" data-testid="contact-hours-display">{cfg.office_hours_display || "Lunes a viernes, 9:00 – 18:00"}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
